// theme/standard.ts
// Brand Colors (same as standard)
const primaryColor = '#24303B';
const secondaryColor = '#1B69B9';
const accentColor = '#D3D6D8';

// Backgrounds (same as standard)
const backgroundBase = '#FFFFFF';
const backgroundSecondary = '#F1F1F1';
const backgroundTertiary = '#E8F4FC';

// Text Colors (same as standard)
const textPrimary = '#24303B';
const textSecondary = '#1B69B9';
const textDisabled = '#A7AEB5';
const textInverse = '#ffffff';
const accenttextColor = '#1B69B9';

// Border and Divider (same as standard)
const borderColor = '#E6E4E4';

// States and Status (same as standard)
const successColor = '#52c41a';
const errorColor = '#ff4d4f';
const warningColor = '#FF0000';
const infoColor = '#1890ff';

// Hover and Active States (same as standard)
const hoverColor = '#e6f7ff';
const activeColor = '#bae7ff';
const selectedColor = '#e6f4ff';

// Drawer Specific (same as standard)
const drawerHeaderBg = '#1B69B9';
const drawerTitleColor = '#ffffff';
const drawerBodyBg = '#ffffff';

export const compactTokens = {
  borderRadius: 2,                    // Reduced from 10 to 2
  fontSize: 12,                       // Reduced from 14 to 12
  colorPrimary: primaryColor,
  colorSecondary: secondaryColor,
  colorBgLayout: backgroundBase,
  colorBgContainer: backgroundSecondary,
  colorText: textPrimary,
  controlHeight: 28,                  // Reduced from 40 to 28
  fontFamily: `'Open Sans', sans-serif`,
  accentColor: accentColor,
  backgroundTertiary: backgroundTertiary,
  colorTextHeading: secondaryColor,
  fontWeightStrong: 600,
  
  // Compact line heights
  lineHeightHeading1: 1.15,          // Reduced from 1.25
  lineHeightHeading2: 1.2,           // Reduced from 1.3
  lineHeightHeading3: 1.25,          // Reduced from 1.35
  lineHeightHeading4: 1.3,           // Reduced from 1.4
  lineHeightHeading5: 1.35,          // Reduced from 1.45

  // Compact font sizes
  fontSizeHeading1: 24,              // Reduced from 32
  fontSizeHeading2: 20,              // Reduced from 28
  fontSizeHeading3: 18,              // Reduced from 24
  fontSizeHeading4: 16,              // Reduced from 20
  fontSizeHeading5: 14,              // Reduced from 16

  // Compact spacing tokens
  paddingXS: 2,                      // Reduced spacing
  paddingSM: 4,
  paddingMD: 8,
  paddingLG: 12,
  paddingXL: 16,
  
  marginXS: 2,
  marginSM: 4,
  marginMD: 8,
  marginLG: 12,
  marginXL: 16,
};

export const compactComponents = {
  Typography: {
    colorTextHeading: secondaryColor,
    titleMarginBottom: 8,              // Reduced from 16
    titleFontWeight: 600,
  },
    
  Button: {
    colorPrimary: secondaryColor,
    colorPrimaryHover: primaryColor,
    defaultBg: backgroundBase,
    defaultColor: accenttextColor,
    colorError: warningColor,
    colorErrorHover: primaryColor,
    borderRadius: 2,                   // Reduced from 3
    fontWeight: 500,
    fontSize: 12,                      // Reduced from 14
    controlHeight: 24,                 // Reduced from 30
    paddingBlock: 2,                   // Reduced from 4
    paddingInline: 8,                  // Reduced from 16
    defaultBorderColor: secondaryColor,
    colorTextDisabled: '#999999',
    colorBgContainerDisabled: '#dddddd',
    colorBorderDisabled: '#d9d9d9',
  },

  Breadcrumb: {
    colorText: secondaryColor,
    fontSize: 14,                      // Reduced from 18
    linkColor: secondaryColor,
    linkHoverColor: secondaryColor,
    separatorColor: borderColor,
    lastItemColor: secondaryColor,
    iconFontSize: 14,                  // Reduced from 18
  },

  Drawer: {
    colorBgElevated: '#ffffff',
    colorText: '#ffffff',
    colorIcon: '#ffffff',
    colorIconHover: '#ffffff',
    paddingLG: 12,                     // Reduced from 24
    headerPadding: '6px 8px',          // Reduced from '10px 14px'
    headerBg: '#000000',
    colorTextHeading: '#001f3f',
    fontWeightStrong: 500,
    footerPadding: '8px 6px',          // Reduced from '14px 12px'
  },

  Table: {
    headerBg: '#ffffff',
    headerColor: '#1B1B1B',
    lineHeight: 1.4,                   // Reduced from 2
    headerFontWeight: 900,
    headerSplitColor: '#ffffff',
    rowHoverBg: 'transparent',
    rowSelectedBg: '#E6F4FF',
    rowSelectedHoverBg: '#D0E9FF',
    colorText: '#24303B',
    borderColor: '#E6E4E4',
    fontSize: 12,                      // Reduced from 13
    padding: 0,
    colorBgContainer: '#ffffff',
    rowExpandedBg: '#FAFCFF',
    headerSortActiveBg: 'transparent',
    headerSortHoverBg: 'transparent',
  },

  Pagination: {
    colorBgContainer: '#fff',
    colorPrimary: '#1B69B9',
    colorPrimaryBorder: '#1B69B9',
    colorText: '#24303B',
    colorTextDisabled: '#ccc',
    colorTextActive: '#1B69B9',
    itemActiveBg: '#E6F4FF',
    itemInputBg: '#fff',
    itemSize: 24,                      // Reduced from 30
    fontSize: 12,                      // Reduced from 13
    borderRadius: 2,                   // Reduced from 3
    borderColorSplit: 'transparent',
    motionDurationMid: '0.2s',
    controlHeight: 24,                 // Reduced from 30
  },
   
  Select: {
    controlHeight: 24,                 // Reduced from 30
    borderRadius: 2,                   // Reduced from 3
    fontSize: 12,                      // Reduced from 14
    colorBorder: '#B0B4B9',
    colorPrimary: '#1B69B9',
    colorText: '#24303B',
    colorTextPlaceholder: '#8C8C8C',
    colorTextDisabled: '#BFBFBF',
    colorBgContainer: '#ffffff',
    colorIcon: '#8C8C8C',
    colorIconHover: '#1B69B9',
    boxShadow: '0 0 0 1px rgba(27, 105, 185, 0.15)', // Thinner shadow
    optionSelectedBg: '#E6F4FF',
    optionSelectedColor: '#1B69B9',
    optionActiveBg: '#F5F5F5',
  },

  Input: {
    controlHeight: 24,                 // Reduced from 32
    borderRadius: 2,                   // Reduced from 3
    colorBorder: '#B0B4B9',
    colorText: '#24303B',
    colorTextPlaceholder: '#8C8C8C',
    colorBgContainer: '#FFFFFF',
    colorPrimary: '#1B69B9',
    colorIcon: '#8C8C8C',
    colorIconHover: '#1B69B9',
    activeShadow: '0 0 0 1px rgba(27, 105, 185, 0.15)', // Thinner shadow
    fontSize: 12,                      // Reduced from 14
  },

  Menu: {
    colorItemText: '#1C1C1C',
    colorItemTextHover: '#1890ff',
    colorItemTextSelected: '#ffffff',
    colorItemBgHover: 'transparent',
    horizontalItemPadding: '6px 12px', // Reduced from '12px 24px'
    itemHeight: 32,                    // Reduced from 48
    itemBorderRadius: 2,               // Reduced from 3
    itemMarginInline: 3,               // Reduced from 5
    fontSize: 12,                      // Reduced from 14
    fontWeight: 700,
    colorBgContainer: '#ffffff',
    colorPrimary: '#1890ff',
    horizontalLineHeight: 32,          // Reduced from 48
    horizontalMargin: '0 8px 0 0',     // Reduced from '0 16px 0 0'
    colorSplit: 'transparent',
  },

  DatePicker: {
    controlHeight: 24,                 // Reduced from 32
    borderRadius: 2,                   // Reduced from 3
    fontSize: 12,                      // Reduced from 14
    colorBorder: '#B0B4B9',
    colorText: '#24303B',
    colorTextPlaceholder: '#8C8C8C',
    colorTextDisabled: '#BFBFBF',
    colorBgContainer: '#FFFFFF',
    colorPrimary: '#1B69B9',
    boxShadow: '0 0 0 1px rgba(27, 105, 185, 0.15)', // Thinner shadow
    colorBgElevated: '#FFFFFF',
    colorTextHeading: '#1B69B9',
    colorTextDescription: '#A7AEB5',
    colorBgBase: '#FFFFFF',
    colorBgContainerSelected: '#E6F4FF',
    colorTextSelected: '#1B69B9',
    colorFillSecondary: '#F5F5F5',
  },

  Checkbox: {
    controlInteractiveSize: 12,        // Reduced from 15
    borderRadiusSM: 2,                 // Reduced from 3
    colorBorder: '#1B69B9',
    colorPrimary: '#1B69B9',
    colorWhite: '#ffffff',
    colorPrimaryHover: '#14589D',
    colorBgContainerChecked: '#1B69B9',
    colorText: '#24303B',
    colorBorderDisabled: '#D9D9D9',
    colorTextDisabled: '#A7AEB5',
    colorBgContainerDisabled: '#F5F5F5',
  },

  Modal: {
    borderRadius: 2,                   // Reduced from 3
    headerBg: '#1B69B9',
    titleColor: '#ffffff',
    titleFontSize: 14,                 // Reduced from 16
    titleFontWeight: 600,
    headerPadding: '8px 16px',         // Reduced from '12px 24px'
    contentBg: '#ffffff',
    bodyPadding: '16px',               // Reduced from '24px'
    footerBg: '#ffffff',
    footerPadding: '8px 16px',         // Reduced from '16px 24px'
    footerBorderTop: '1px solid #E6E4E4',
    closeIconColor: '#ffffff',
    closeBtnSize: 32,                  // Reduced from 48
  },

  Dropdown: {
    colorBgContainer: '#ffffff',
    borderRadius: 2,                   // Reduced from 3
    borderColor: '#d9d9d9',
    colorText: '#1f1f1f',
    fontSize: 12,                      // Reduced from 14
    controlHeight: 28,                 // Reduced from 36
    controlPaddingHorizontal: 8,       // Reduced from 12
    controlItemBgHover: '#f5f5f5',
    controlItemBgActive: '#e6f7ff',
    controlItemBgSelected: '#e6f7ff',
    controlItemColorActive: '#1677ff',
    colorTextDisabled: '#bfbfbf',
  },

  Radio: {
    colorPrimary: '#1B69B9',
    colorText: '#24303B',
    colorBorder: '#D9D9D9',
    dotSize: 4,                        // Reduced from 6
    buttonSolidCheckedActiveBg: '#1B69B9',
    buttonSolidCheckedLabelColor: '#ffffff',
  },

  Tooltip: {
    colorBgDefault: '#1B69B9',
    colorText: '#FFFFFF',
    colorBorder: '#1B69B9',
    borderRadius: 2,                   // Reduced from 3
    fontSize: 12,                      // Reduced from 12
    paddingBlock: 4,                   // Reduced from 6
    paddingInline: 8,                  // Reduced from 12
  },

  Switch: {
    colorPrimary: '#1B69B9',
    colorPrimaryHover: '#1B69B9',
    colorTextTertiary: '#1B69B9',
    handleSize: 12,                    // Reduced from 16
    trackHeight: 16,                   // Reduced from 22
    trackMinWidth: 32,                 // Reduced from 44
    borderRadius: 16,                  // Reduced from 20
  },

  Tag: {
    colorBgContainer: '#ffffff',
    colorBorder: '#1890ff',
    colorText: '#1890ff',
    fontWeightStrong: 600,
    fontSize: 12,                      // Reduced from 14
    paddingInline: 6,                  // Reduced from 10
    paddingBlock: 2,                   // Reduced from 4
    borderRadius: 3,                   // Reduced from 6
    controlHeight: 20,                 // Reduced from 28
    colorIcon: '#ff4d4f',
    colorIconHover: '#d9363e',
  },

  Collapse: {
    headerBg: '#1B69B9',
    contentBg: '#FFFFFF',
    colorText: '#24303B',
    colorTextHeading: '#ffffff',
    colorBorder: '#D9D9D9',
    headerPadding: '8px 12px',         // Reduced from '12px 16px'
    contentPadding: '12px',            // Reduced from '16px'
    headerHeight: 36,                  // Reduced from 48
    fontSize: 12,                      // Reduced from 14
    fontSizeIcon: 12,                  // Reduced from 12
    borderRadiusLG: 2,                 // Reduced from 3
    colorIcon: '#1B69B9',
    colorBgContainer: '#fff',
  },

  Tabs: {
    colorText: '#24303B',
    colorPrimary: '#1B69B9',
    colorBgContainer: '#24303B',
    itemColor: '#24303B',
    itemSelectedColor: '#ffffff',
    itemHoverColor: '#1B69B9',
    itemActiveColor: '#1B69B9',
    inkBarColor: '#1B69B9',
    horizontalItemPadding: '4px 8px',  // Reduced from '8px 16px'
    verticalItemPadding: '4px 8px',    // Reduced from '8px 16px'
    fontSize: 12,                      // Reduced from 14
    lineHeight: 1.3,                   // Reduced from 1.5
    cardBg: '#ffffff',
    cardGutter: 2,                     // Reduced from 3
    cardHeight: 32,                    // Reduced from 40
    borderRadiusLG: 2,                 // Reduced from 3
  },
};